<script setup>
import {ref, onMounted, computed, watch, onUnmounted} from 'vue'
import {useRouter, useRoute} from 'vue-router'
import axios from 'axios'
import { createExamClock } from '@/composables/examClock'
import { formatTime12 } from '@/composables/examTime'

const router = useRouter()
const route = useRoute()

const exams = ref([])
const loading = ref(true)

const user = JSON.parse(localStorage.getItem('user') || '{}')
const education_level = user.education_level || 4

// ── Server-authoritative MONOTONIC clock ────────────────────────────────────
// At every synchronization we pin an absolute server instant (server_now) to a
// monotonic reading (performance.now()). The displayed "now" then advances by the
// monotonic delta ONLY — it never reads the device wall clock again, so manually
// changing the phone time / NTP corrections / long sleeps cannot shift the exam
// countdown. A fresh API sync (on visibility/focus/pageshow) re-pins both points.
const synchronizedClock = createExamClock()
const nowTick = ref(0)
const perfNow = () => performance.now()
const serverNow = () => { void nowTick.value; return synchronizedClock.now() }

/* ─── Computed ─── */
const durationRequired = computed(() => route.query.type === 'online' ? 1 : 0)
const isOnline = computed(() => route.query.type === 'online')
const pageTitle = computed(() => isOnline.value ? 'الامتحانات الأونلاين' : 'الواجبات المنزلية')
const pageIcon  = computed(() => isOnline.value ? '📝' : '🏠')

/* ─── Helpers ─── */
// Absolute exam start/end come from the backend as ISO-8601 with a +03:00 offset
// (Africa/Cairo), so new Date(...) yields the exact instant on ANY device. When they
// are null (untimed homework) there is no window: never "ended", always "started".
const examStartMs = (exam) => (exam.exam_start_at ? new Date(exam.exam_start_at).getTime() : null)
const examEndMs = (exam) => (exam.effective_exam_end_at || exam.exam_end_at) ? Date.parse(exam.effective_exam_end_at || exam.exam_end_at) : null

// Availability is decided from SERVER truth, never the device clock:
//  • Before the monotonic clock is anchored to a server sync, trust the
//    server-computed exam_status from the payload (identical for every student).
//  • After a sync, serverNow() is anchored to server_now and advances monotonically,
//    so the per-second boundary flip stays server-authoritative too.
// The device wall clock is never used to mark an exam ended/started.
const clockSynced = () => synchronizedClock.now() !== null

const isExamEnded = (exam) => {
  const end = examEndMs(exam)
  if (end == null) return false                       // untimed homework → never "ended"
  if (!clockSynced()) return exam.exam_status === 'expired'
  return serverNow() >= end                           // server-anchored time vs fixed end
}
const isExamStarted = (exam) => {
  const start = examStartMs(exam)
  if (start == null) return true                      // untimed → always "started"
  if (!clockSynced()) return exam.exam_status !== 'not_started'
  return serverNow() >= start
}
const formatDate = (dateString) =>
    new Date(dateString).toLocaleDateString('ar-EG', { year:'numeric', month:'long', day:'numeric' })

const examStatus = (exam) => {
  // Completed takes priority over "ended": a submitted single-attempt exam
  // still opens to its Result Summary after the window closes, so the badge
  // must reflect that instead of a dead-looking "ended" state.
  if (exam.taken && isOnline.value) return { label: 'تم التأدية', cls: 'taken' }
  if (isExamEnded(exam)) return { label: 'انتهى', cls: 'ended' }
  if (!isExamStarted(exam)) return { label: 'لم يبدأ بعد', cls: 'pending' }
  return { label: 'متاح الآن', cls: 'live' }
}

const canStart = (exam) =>
    isExamStarted(exam) && !isExamEnded(exam) &&
    (!exam.taken || !isOnline.value)

// A single-attempt online exam the student already completed can still be
// opened — the exam page detects the submitted attempt and shows the Result
// Summary instead of the quiz (never a dead end / disabled card with no way
// to see the score again).
const canOpen = (exam) => canStart(exam) || (exam.taken && isOnline.value)

/* ─── Server-time synchronization ───────────────────────────────────────────
 * Reuses the SAME /exams_web endpoint currentExam.vue already uses. Called on
 * initial load and whenever the app becomes active again (visibility/focus/
 * pageshow). Throttled so overlapping activation events send ONE request; a
 * sequence id guards against an older response overwriting a newer sync.
 */
let syncSeq = 0            // increments per sync; only the latest seq may apply
let lastSyncAt = 0        // perfNow() of the last STARTED sync (throttle window)
const SYNC_THROTTLE_MS = 1500

const applyServerNow = (iso) => synchronizedClock.sync(iso)

const syncServerTime = async ({ force = false, showLoading = false } = {}) => {
  const nowM = perfNow()
  if (!force && (nowM - lastSyncAt) < SYNC_THROTTLE_MS) return // debounce duplicate triggers
  lastSyncAt = nowM
  const seq = ++syncSeq
  if (showLoading) loading.value = true
  try {
    const response = await axios.get('/exams_web', {
      params: { education_level, duration_required: durationRequired.value }
    })
    if (seq !== syncSeq) return          // a newer sync has since started/won — drop stale response
    exams.value = response.data.data || []
    applyServerNow(response.data.server_now)
  } catch (e) {
    if (seq !== syncSeq) return
    console.error(e)
    // On the INITIAL load failure keep the old empty-state behavior; on a background
    // re-sync failure keep the last known exams + authoritative time (never reset the
    // clock or mark the exam ended just because a sync request failed).
    if (showLoading) exams.value = []
  } finally {
    if (showLoading) loading.value = false
  }
}

/* ─── Actions ─── */
const goToExam = (exam) => {
  if (!canOpen(exam)) return
  router.push(`/exam/${exam.id}?type=${exam.duration_required === 1 ? 'online' : 'homework'}`)
}

/* ─── Access tracking (Part 1: exams_page_view) ──────────────────────────────
 * Records ONE "opened the online exams page" event per visit. Fired on mount and
 * when the user switches INTO the online tab — never on the background re-syncs
 * (visibility/focus/pageshow), so Vue re-renders / re-fetches don't create
 * duplicates. Online page only (homework is skipped). The server stamps the time;
 * a tracking failure is swallowed so it can never block the exams UI. */
let pageViewTracked = false
const trackExamsPageView = () => {
  if (!isOnline.value || pageViewTracked) return
  pageViewTracked = true
  axios.post('/exam-access/page-view').catch(() => {
    // Passive: never surface a tracking hiccup to the student.
  })
}

/* ─── Re-sync triggers (app becomes active again) ─── */
const onVisibility = () => { if (document.visibilityState === 'visible') syncServerTime() }
const onFocus = () => syncServerTime()
const onPageShow = () => syncServerTime() // fires on bfcache/WebView restore of a suspended page

/* ─── Lifecycle ─── */
let tickInterval = null
let unwatchType = null
onMounted(() => {
  // Initial load fetches exams + pins the authoritative clock.
  syncServerTime({ force: true, showLoading: true })

  // Record the page visit (online exams page only; once per visit).
  trackExamsPageView()

  // ONE ticker: bump nowTick each second so status flips live across the start/end
  // boundary. It does NOT read the wall clock — serverNow() advances via performance.now().
  tickInterval = setInterval(() => { nowTick.value++ }, 1000)

  // Re-synchronize with the server whenever the app/page becomes active again, so a
  // long background period or a device-clock change is corrected immediately.
  document.addEventListener('visibilitychange', onVisibility)
  window.addEventListener('focus', onFocus)
  window.addEventListener('pageshow', onPageShow)

  // Switching type (online ↔ homework) changes the query params → force a fresh fetch.
  // A switch is a NEW visit context: reset the guard, then track if we're now online.
  unwatchType = watch(() => route.query.type, () => {
    pageViewTracked = false
    syncServerTime({ force: true, showLoading: true })
    trackExamsPageView()
  })
})
onUnmounted(() => {
  if (tickInterval) clearInterval(tickInterval)
  if (unwatchType) unwatchType()
  document.removeEventListener('visibilitychange', onVisibility)
  window.removeEventListener('focus', onFocus)
  window.removeEventListener('pageshow', onPageShow)
})
</script>

<template>
  <div class="exams-page">
    <div class="container">

      <!-- Page header -->
      <div class="exams-header">
        <div class="exams-header-icon">{{ pageIcon }}</div>
        <h1 class="exams-header-title">{{ pageTitle }}</h1>
        <p class="exams-header-sub">
          {{ isOnline ? 'اختر الامتحان الذي تريد البدء به' : 'الواجبات المطلوبة منك هذه الفترة' }}
        </p>
      </div>

      <!-- Loading skeleton -->
      <div v-if="loading" class="exams-grid">
        <div v-for="n in 6" :key="n" class="exam-skeleton">
          <div class="skel skel-head"></div>
          <div class="skel skel-line"></div>
          <div class="skel skel-line skel-short"></div>
          <div class="skel skel-btn"></div>
        </div>
      </div>

      <!-- Empty state -->
      <div v-else-if="!exams.length" class="exams-empty">
        <div class="empty-icon">{{ isOnline ? '📋' : '📚' }}</div>
        <h2>لا توجد عناصر حالياً</h2>
        <p>سيتم إضافة {{ isOnline ? 'امتحانات' : 'واجبات' }} قريباً، راجع لاحقاً</p>
      </div>

      <!-- Exam cards -->
      <div v-else class="exams-grid">
        <div
          v-for="exam in exams"
          :key="exam.id"
          class="exam-card"
          :class="[
            { 'exam-card--disabled': !canOpen(exam) },
            `exam-card--${examStatus(exam).cls}`
          ]"
          @click="goToExam(exam)"
        >
          <!-- Card header with gradient -->
          <div class="exam-card-header" :class="isOnline ? 'grad-primary' : 'grad-accent'">
            <div class="exam-card-type-badge">
              {{ isOnline ? 'امتحان' : 'واجب' }}
            </div>
            <h3 class="exam-card-title">{{ exam.title }}</h3>
            <p class="exam-card-lesson">{{ exam.lesson?.title || 'بدون درس' }}</p>
          </div>

          <!-- Status badge -->
          <div class="exam-status-wrap">
            <span class="exam-status" :class="`exam-status--${examStatus(exam).cls}`">
              <span class="exam-status-dot"></span>
              {{ examStatus(exam).label }}
            </span>
          </div>

          <!-- Card body -->
          <div class="exam-card-body">
            <div class="exam-info-list">
              <div class="exam-info-item" v-if="exam.personal_duration_minutes">
                <span class="exam-info-icon">⏱</span>
                <span>مدة شخصية: {{ exam.personal_duration_minutes }} دقيقة، بحد أقصى نهاية الوقت العام</span>
              </div>
              <div class="exam-info-item">
                <span class="exam-info-icon">📅</span>
                <span>{{ formatDate(exam.date) }}</span>
              </div>
              <div class="exam-info-item" v-if="exam.from_time">
                <span class="exam-info-icon">🕒</span>
                <span>{{ formatTime12(exam.from_time) }} — {{ formatTime12(exam.to_time) }}</span>
              </div>
              <div class="exam-info-item">
                <span class="exam-info-icon">🎯</span>
                <span>الدرجة الكلية: <strong>{{ exam.total_degree }}</strong></span>
              </div>
              <div class="exam-info-item">
                <span class="exam-info-icon">❓</span>
                <span>{{ exam.questions?.length || 0 }} سؤال</span>
              </div>
            </div>

            <!-- Start button -->
            <button
              v-if="canStart(exam)"
              class="exam-start-btn"
              :class="isOnline ? 'btn-primary' : 'btn-accent'"
              @click.stop="goToExam(exam)"
            >
              {{ isOnline ? '🚀 ابدأ الامتحان' : '📖 عرض الواجب' }}
            </button>

            <!-- Already completed (single-attempt online exam) — always opens the
                 Result Summary, even if the exam window has since ended. -->
            <button
              v-else-if="exam.taken && isOnline"
              class="exam-start-btn btn-primary"
              @click.stop="goToExam(exam)"
            >
              📊 عرض النتيجة
            </button>

            <!-- Ended notice (not taken) -->
            <div v-else-if="isExamEnded(exam)" class="exam-notice exam-notice--red">
              ⏰ انتهى وقت {{ isOnline ? 'الامتحان' : 'الواجب' }}
            </div>
          </div>

        </div>
      </div>

    </div>
  </div>
</template>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Cairo:wght@400;600;700;800&display=swap');

.exams-page {
  padding: 2rem 0 4rem;
  min-height: calc(100vh - 70px);
  font-family: 'Cairo', sans-serif;
  direction: rtl;
  background: #F5F7FB;
}

.container { max-width: 1200px; margin: 0 auto; padding: 0 1.25rem; }

/* ─── Header ─── */
.exams-header {
  text-align: center; padding: 3rem 1rem 2.5rem;
}
.exams-header-icon { font-size: 3rem; margin-bottom: .75rem; }
.exams-header-title {
  font-size: 2.2rem; font-weight: 800; margin-bottom: .5rem;
  background: linear-gradient(135deg,#5B5FEF,#7C4DFF);
  -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text;
}
.exams-header-sub { color: #6B7280; font-size: 1rem; }

/* ─── Grid ─── */
.exams-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 1.5rem;
}

/* ─── Skeleton ─── */
.exam-skeleton {
  background: #fff; border-radius: 20px; overflow: hidden;
  box-shadow: 0 4px 12px rgba(0,0,0,.06);
}
.skel {
  background: linear-gradient(90deg,#f0f0f0 25%,#e0e0e0 50%,#f0f0f0 75%);
  background-size: 200% 100%;
  animation: shimmer 1.4s infinite; border-radius: 8px;
}
@keyframes shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }
.skel-head { height: 110px; border-radius: 0; }
.skel-line { height: 14px; margin: 1rem 1.25rem .5rem; }
.skel-short { width: 60%; }
.skel-btn { height: 44px; margin: .75rem 1.25rem 1.5rem; border-radius: 12px; }

/* ─── Empty ─── */
.exams-empty {
  text-align: center; padding: 5rem 1rem;
  color: #6B7280;
}
.empty-icon { font-size: 4rem; margin-bottom: 1rem; }
.exams-empty h2 { font-size: 1.5rem; font-weight: 700; color: #1A1A1A; margin-bottom: .5rem; }
.exams-empty p { font-size: .95rem; }

/* ─── Card ─── */
.exam-card {
  background: #fff; border-radius: 20px;
  box-shadow: 0 4px 16px rgba(0,0,0,.07);
  cursor: pointer; transition: all .35s cubic-bezier(.4,0,.2,1);
  border: 2px solid transparent; overflow: hidden;
  position: relative;
}
.exam-card:hover:not(.exam-card--disabled) {
  transform: translateY(-8px);
  box-shadow: 0 20px 50px rgba(91,95,239,.15);
  border-color: rgba(91,95,239,.2);
}
.exam-card--disabled { opacity: .55; cursor: not-allowed; }
.exam-card--disabled:hover { transform: none; box-shadow: 0 4px 16px rgba(0,0,0,.07); border-color: transparent; }

/* Card header */
.exam-card-header {
  padding: 1.5rem 1.25rem 1.25rem;
  color: #fff; position: relative;
}
.grad-primary { background: linear-gradient(135deg,#5B5FEF 0%,#7C4DFF 100%); }
.grad-accent  { background: linear-gradient(135deg,#00C2FF 0%,#00D4A6 100%); }

.exam-card-type-badge {
  display: inline-block; background: rgba(255,255,255,.2);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255,255,255,.3);
  border-radius: 99px; padding: .2rem .75rem;
  font-size: .75rem; font-weight: 700; margin-bottom: .75rem;
}
.exam-card-title { font-size: 1.2rem; font-weight: 800; margin-bottom: .35rem; line-height: 1.3; }
.exam-card-lesson { font-size: .85rem; opacity: .88; margin: 0; }

/* Status */
.exam-status-wrap { padding: .75rem 1.25rem 0; }
.exam-status {
  display: inline-flex; align-items: center; gap: .4rem;
  font-size: .8rem; font-weight: 700; padding: .3rem .9rem;
  border-radius: 99px;
}
.exam-status-dot { width: 7px; height: 7px; border-radius: 50%; animation: pulse 1.5s infinite; flex-shrink: 0; }
.exam-status--live   { background: rgba(34,197,94,.12);  color: #16a34a; }
.exam-status--live .exam-status-dot { background: #22C55E; }
.exam-status--ended  { background: rgba(239,68,68,.1);   color: #dc2626; }
.exam-status--ended .exam-status-dot { background: #EF4444; animation: none; }
.exam-status--taken  { background: rgba(91,95,239,.1);   color: #5B5FEF; }
.exam-status--taken .exam-status-dot { background: #5B5FEF; animation: none; }
.exam-status--pending{ background: rgba(245,158,11,.1);  color: #b45309; }
.exam-status--pending .exam-status-dot { background: #F59E0B; }
@keyframes pulse { 0%,100%{opacity:1} 50%{opacity:.3} }

/* Card body */
.exam-card-body { padding: 1rem 1.25rem 1.5rem; }
.exam-info-list { display: flex; flex-direction: column; gap: .5rem; margin-bottom: 1.25rem; }
.exam-info-item { display: flex; align-items: center; gap: .6rem; color: #475569; font-size: .9rem; }
.exam-info-icon { font-size: 1rem; flex-shrink: 0; }

/* Buttons */
.exam-start-btn {
  width: 100%; padding: .85rem;
  border: none; border-radius: 14px;
  font-size: 1rem; font-weight: 700;
  cursor: pointer; transition: all .3s;
  font-family: 'Cairo', sans-serif;
}
.btn-primary { background: linear-gradient(135deg,#5B5FEF,#7C4DFF); color: #fff; box-shadow: 0 8px 20px rgba(91,95,239,.25); }
.btn-primary:hover { box-shadow: 0 14px 30px rgba(91,95,239,.4); transform: translateY(-2px); }
.btn-accent  { background: linear-gradient(135deg,#00C2FF,#00D4A6); color: #fff; box-shadow: 0 8px 20px rgba(0,212,166,.2); }
.btn-accent:hover  { box-shadow: 0 14px 30px rgba(0,212,166,.35); transform: translateY(-2px); }

/* Notices */
.exam-notice {
  text-align: center; padding: .75rem; border-radius: 12px;
  font-weight: 700; font-size: .9rem;
}
.exam-notice--red  { background: rgba(239,68,68,.08);  color: #dc2626; }
.exam-notice--gray { background: rgba(91,95,239,.07);  color: #5B5FEF; }

@media(max-width:768px) {
  .exams-page { padding: 1rem 0 3rem; }
  .exams-header { padding: 1.25rem 1rem 1.35rem; }
  .exams-header-icon { font-size: 2.4rem; margin-bottom: .5rem; }
  .exams-header-title { font-size: 1.75rem; line-height: 1.3; margin-bottom: .35rem; }
  .exams-header-sub { font-size: .88rem; line-height: 1.5; }
}

@media(max-width:600px) {
  .exams-header-title { font-size: 1.7rem; }
  .exams-grid { grid-template-columns: 1fr; }
}
</style>
